'use client';

/**
 * AffiliateRefCapture — single mount in the root layout.
 *
 * On first load, reads ?ref=CODE from the URL (if present), stores it in
 * localStorage as cs_ref, and fires a one-shot track-click POST to the
 * backend so the affiliate's "clicks" count goes up. Subsequent loads of
 * the app reuse the stored code so checkout can attach the right Stripe
 * promotion code automatically.
 *
 * Persistence: localStorage only (no cookie). 30-day TTL enforced by the
 * stored timestamp — anything older gets pruned on next load.
 */
import { useEffect } from 'react';

const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const API_URL = process.env.NEXT_PUBLIC_API_URL || '';

export default function AffiliateRefCapture() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Prune expired ref so a long-dormant tab doesn't keep stale credit
    try {
      const ts = Number(localStorage.getItem('cs_ref_at') || '0');
      if (ts && Date.now() - ts > TTL_MS) {
        localStorage.removeItem('cs_ref');
        localStorage.removeItem('cs_ref_at');
      }
    } catch { /* private mode */ }

    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;

    try {
      localStorage.setItem('cs_ref', ref);
      localStorage.setItem('cs_ref_at', String(Date.now()));
    } catch { /* private mode */ }

    // Fire-and-forget click tracking. Public endpoint, no auth needed.
    fetch(`${API_URL}/api/affiliate/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code: ref,
        referer: document.referrer || null,
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
      }),
      keepalive: true,
    }).catch(() => { /* never breaks the app */ });
  }, []);

  return null;
}
